import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, toast } from "@dl/web-core-js/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProductionOrderComponentClient, type ProductionOrderComponentDTO } from "../../../api/floor";
import { formatNumber, refusalMessage } from "../../../api/manufacturing";
import { OpenRecord } from "../../../components/OpenRecord";
import { percent, ProgressBar } from "../../../components/ProgressBar";

/**
 * The lot's components as released: the part, its letter, how many, how many
 * cutboards and pallets that makes, and how far each is. Only the note is
 * edited here; the rest is what the lot was released with. A newer drawing
 * than the one released is flagged: reprinting the traveller would still use
 * the released one.
 */
export function ComponentsTab({
  components,
  loading,
  editable,
  onChanged,
}: {
  components: ProductionOrderComponentDTO[];
  loading: boolean;
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ProductionOrderComponentClient(http), [http]);

  const cols = useMemo(() => {
    const c = defineColumns<ProductionOrderComponentDTO>();
    return [
      c.text("code", (row) => row.code, { headerKey: "lot.component.code", priority: 1 }),
      c.custom(
        "letter",
        (row) => row.letter ?? "",
        (row) =>
          row.letter ? (
            <Badge variant="accent" size="sm">
              {row.letter}
            </Badge>
          ) : null,
        { headerKey: "lot.component.letter", priority: 1 },
      ),
      c.custom(
        "part",
        (row) => row.part_number ?? "",
        (row) => (
          <span>
            <span className="font-medium">{row.part_number}</span>
            <span className="ml-2 text-[11px] text-ink-faint">{row.part_name}</span>
            {row.has_newer_drawing && (
              <Badge variant="warning" size="sm" className="ml-2">
                {t("lot.component.newer-drawing", "newer drawing")}
              </Badge>
            )}
          </span>
        ),
        { headerKey: "column.part", priority: 1 },
      ),
      c.number("quantity", (row) => row.quantity, { headerKey: "column.mo.quantity", priority: 1 }),
      c.text("cutboard_quantity", (row) => (row.parts_per_cutboard != null && row.parts_per_cutboard > 1 ? `${formatNumber(row.cutboard_quantity)} × ${String(row.parts_per_cutboard)}` : ""), {
        headerKey: "lot.component.cutboards",
        priority: 3,
      }),
      c.text("pallet_count", (row) => (row.pallet_quantity ? `${String(row.pallet_count ?? 1)} × ${String(row.pallet_quantity)}` : String(row.pallet_count ?? 1)), {
        headerKey: "lot.component.pallets",
        priority: 3,
      }),
      c.custom(
        "progress",
        (row) => row.progress ?? 0,
        (row) => (
          <span className="flex items-center gap-2">
            <ProgressBar value={row.progress} tone={(row.progress ?? 0) >= 1 ? "done" : "active"} className="w-20" />
            <span className="text-[12px] tabular-nums text-ink-muted">
              {percent(row.progress)} · {row.step_done_count ?? 0}/{row.step_count ?? 0}
            </span>
          </span>
        ),
        { headerKey: "lot.overview.progress", priority: 2 },
      ),
      c.number("quantity_done", (row) => row.quantity_done, { headerKey: "lot.component.ready", priority: 2 }),
      c.custom(
        "quantity_missing",
        (row) => row.quantity_missing ?? 0,
        (row) =>
          Number(row.quantity_missing ?? 0) > 0 ? (
            <Badge variant="error" size="sm">
              {formatNumber(row.quantity_missing)}
            </Badge>
          ) : null,
        { headerKey: "lot.step.missing", priority: 3 },
      ),
      c.text("sigm_project_number", (row) => (row.sigm_project_number == null ? "" : String(row.sigm_project_number)), {
        headerKey: "lot.component.sigm",
        defaultVisible: false,
      }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 4 }),
    ];
  }, [t]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ProductionOrderComponentDTO>
        data={components}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={loading}
        rowActions={(row) => <OpenRecord target={{ kind: "part", id: row.part_id }} label={row.part_number ?? ""} />}
        editor={
          editable
            ? {
                fields: {
                  note: { kind: "text", get: (row) => row.note, set: (row, value) => ({ ...row, note: value === "" ? null : value }) },
                },
                onSave: async (row) => {
                  try {
                    await client.update(row);
                  } catch (failure) {
                    throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                  }
                  await onChanged();
                  toast.success(t("notification.saved", "Saved"));
                },
              }
            : undefined
        }
        emptyMessage={t("lot.component.empty", "No components: they are written when the lot is released.")}
      />
    </div>
  );
}
